import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import Encoding from 'encoding-japanese';
import ErrorDisplay from './components/ErrorDisplay';
import NotificationModal from './NotificationModal';
import { logActivity } from './lib/activityLogger';
import { useUser } from './hooks/UserContext';

function DeleteCustomer() {
  const { t, i18n } = useTranslation(['deleteCustomer', 'home']);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(t('selectFile'));
  const [result, setResult] = useState('');
  const [errors, setErrors] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [stripeEnv, setStripeEnv] = useState(null);
  const fileInputRef = useRef(null);
  
  const [userToken, setUserToken] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const { user } = useUser();

  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationModalContent, setNotificationModalContent] = useState({ title: '', message: '' });

  useEffect(() => {
    setFileName(t('selectFile'));
  }, [t]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setUserToken(token);
        } catch (error) {
          console.error("Error getting user token:", error);
          setUserToken(null);
        }
      } else {
        setUserToken(null);
      }
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    if (!userToken) {
      throw new Error('No user token available. Please log in.');
    }
    const newOptions = { ...options };
    newOptions.headers = { ...newOptions.headers, Authorization: `Bearer ${userToken}` };
    if (options.body && typeof options.body !== 'string' && !(options.body instanceof FormData) && !(options.body instanceof Blob)) {
        newOptions.headers['Content-Type'] = 'application/json';
        newOptions.body = JSON.stringify(options.body);
    }

    const response = await fetch(url, newOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let error;
      try {
        const errorJson = JSON.parse(errorText);
        error = new Error(errorJson.error || `Request failed with status ${response.status}`);
      } catch (e) {
        error = new Error(errorText || `Request failed with status ${response.status}`);
      }
      throw error;
    }
    return response;
  }, [userToken]);

  const checkApiKeyStatus = useCallback(async () => {
    if (!authInitialized || !userToken) return false;
    try {
      const response = await fetchWithAuth('/v1/stripe-api-key-status');
      const data = await response.json();
      if (!data.exists) {
        setStripeEnv(null);
        setNotificationModalContent({
          title: t('home:stripeApiKeyWarningTitle'),
          message: t('home:stripeApiKeyWarning'),
        });
        setIsNotificationModalOpen(true);
        return false;
      }
      if (data.environment) {
        setStripeEnv(data.environment);
      } else {
        setStripeEnv(null);
      }
      return true;
    } catch (error) {
      console.error('API key status error:', error.message);
      setErrors([{ message: 'APIキーのステータス確認中にエラーが発生しました。' }]);
      return false;
    }
  }, [authInitialized, userToken, fetchWithAuth, t]);

  useEffect(() => {
    if (authInitialized && userToken) {
      checkApiKeyStatus();
    }
  }, [authInitialized, userToken, checkApiKeyStatus]);

  const handleFile = (selectedFile) => {
    if (selectedFile) {
        const fName = selectedFile.name.toLowerCase();
        if (!fName.endsWith('.csv')) {
            setResult(t('invalidFileType'));
            setFile(null);
            setFileName(t('noFileSelected'));
            setDownloadUrl(null);
            setErrors([]);
            return;
        }
        setFile(selectedFile);
        setFileName(selectedFile.name);
        setDownloadUrl(null);
        setResult('');
        setErrors([]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
        handleFile(e.target.files[0]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFile(e.dataTransfer.files[0]);
        if (fileInputRef.current) {
            fileInputRef.current.files = e.dataTransfer.files;
        }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (!file) {
        setResult(t('selectFileError'));
        setIsSubmitting(false);
        isSubmittingRef.current = false;
        return;
      }

      const apiKeyExists = await checkApiKeyStatus();
      if (!apiKeyExists) {
        setIsSubmitting(false);
        isSubmittingRef.current = false;
        return;
      }

      setResult(t('processing'));
      setErrors([]);
      setDownloadUrl(null);

      const buffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(buffer);
      const detectedEncoding = Encoding.detect(uint8Array);
      const decoder = new TextDecoder(detectedEncoding === 'SJIS' ? 'shift-jis' : 'utf-8');
      const csvText = decoder.decode(uint8Array);

      const response = await fetchWithAuth('/v1/customers/delete', {
        method: 'POST',
        body: { 
          csv: csvText, 
          language: i18n.language, 
          fileName: file.name
        }
      });

      const resultData = await response.json();
      const errorRecords = resultData.error_records ? JSON.parse(resultData.error_records) : [];

      if (errorRecords.length > 0) {
        setErrors(errorRecords);
        setResult(t('deleteResult', { total: resultData.totalRecords, success: resultData.successCount, errors: resultData.errorCount }));
        logActivity({
          billwaveCustomerId: user.billwaveCustomerId,
          action: 'customer_delete',
          result: 'failed',
          target: { type: 'customer_csv', name: file.name },
          actor: { uid: user.uid, email: user.email, name: user.representativeName },
          details: { ...resultData, error_records: errorRecords }
        });
      } else {
        setResult(t('deleteResult', { total: resultData.totalRecords, success: resultData.successCount, errors: 0 }));
        setErrors([]);
        logActivity({
          billwaveCustomerId: user.billwaveCustomerId,
          action: 'customer_delete',
          result: 'success',
          target: { type: 'customer_csv', name: file.name },
          actor: { uid: user.uid, email: user.email, name: user.representativeName },
          details: resultData
        });
      }

      if (resultData.result_csv_buffer) {
        const resultCsvBuffer = new Uint8Array(resultData.result_csv_buffer.data || []);
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, resultCsvBuffer], { type: 'text/csv;charset=utf-8;' });
        setDownloadUrl(URL.createObjectURL(blob));
      }
    } catch (error) {
      try {
        const errorData = JSON.parse(error.message);
        setErrors(Array.isArray(errorData.error) ? errorData.error : [{ message: errorData.error || error.message }]);
      } catch (e) {
        setErrors([{ message: error.message }]);
      }
      setResult('');
      logActivity({
        billwaveCustomerId: user?.billwaveCustomerId,
        action: 'customer_delete',
        result: 'failed',
        target: { type: 'customer_csv', name: file?.name },
        actor: { uid: user?.uid, email: user?.email, name: user?.representativeName },
        details: { error: error.message }
      });
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      setFile(null);
      setFileName(t('selectFile'));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const closeNotificationModal = () => setIsNotificationModalOpen(false);
  
  if (!authInitialized) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl text-gray-500">{t('authLoading')}</p>
      </div>
    );
  }

  if (!userToken) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">{t('home:authTitle')}</h1>
              <p className="text-gray-700">{t('home:authDescription')}</p>
          </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
      <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
        <h1 className="text-3xl font-bold text-gray-900">{t('mainTitle')}</h1>
        {stripeEnv && (
          <div className="flex items-center space-x-2 text-sm bg-gray-50 px-3 py-1.5 border rounded-md">
            <span className="font-semibold text-gray-700">Stripe環境:</span>
            <span className={`font-bold px-2 py-0.5 rounded ${stripeEnv === 'live' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {stripeEnv === 'live' ? '本番' : 'テスト'}
            </span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
        <div 
          className="flex flex-col items-center justify-center p-6 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L6.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span className="text-gray-500">{fileName || t('selectFile')}</span>
          <input 
            type="file" 
            accept=".csv" 
            className="hidden" 
            ref={fileInputRef} 
            onChange={handleFileChange}
          />
        </div>
        <button 
          type="submit" 
          disabled={isSubmitting}
          className="bg-red-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-red-700 transition-colors shadow-md disabled:bg-red-400 disabled:cursor-not-allowed"
          >
          {isSubmitting ? t('submitButtonLoading') : t('submitButton')}
        </button>
      </form>
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-2 pb-2 border-b-2 border-gray-200">{t('resultTitle')}</h2>
        <ErrorDisplay errors={errors} />
        {result && (
          <pre className="bg-gray-100 text-gray-900 p-4 rounded-lg overflow-x-auto text-sm mt-4 whitespace-pre-wrap">{result}</pre>
        )}
        {downloadUrl && (
          <a
            href={downloadUrl}
            download="customer_delete_result.csv"
            className="mt-4 inline-block bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
          >
            {t('downloadButton')}
          </a>
        )}
      </div>

      <NotificationModal
          isOpen={isNotificationModalOpen}
          onClose={closeNotificationModal}
          title={notificationModalContent.title}
      >
          <p>{notificationModalContent.message}</p>
      </NotificationModal>
    </div>
  );
}

export default DeleteCustomer;
